import groupBy from 'lodash/groupBy';
import uniqBy from 'lodash/uniqBy';
import { EQUIPMENT_CATEGORY_FILTERS, INVENTORY_PROPERTIES, PROPERTY_NOT_FOUND, TRAILER_NUMBER_FIELDS, isUnknownSentinel } from 'utils/constants';
import { readFromObj } from 'utils/utils';

const ALERT_NAME_BANNED = 'Banned';
// Exported so the dropdown can skip rendering the grey "Default" group, which
// is the absence of a real alert rather than an alert worth a row.
export const ALERT_NAME_DEFAULT = 'Default';

export const getAlertsFromAccessEquipments = (accessEquipments = []) =>
    accessEquipments.map(({ authType, accessPeriod, note }) => {
        return {
            name: authType?.name || 'Default',
            color: authType?.color || '#747692',
            note: authType?.description,
            customNote: note,
            accessPeriod: accessPeriod,
        };
    });

/**
 * A stable identity for an alert badge: everything the badge and its Note can show.
 *
 * Also used as the React key. `Alerts` used to key on text + color alone, which collided on every
 * equipment whose access rows shared an authorization type.
 */
export const getAlertKey = (alert) =>
    [alert?.text, alert?.name, alert?.color, alert?.note, alert?.customNote, alert?.accessPeriod && JSON.stringify(alert.accessPeriod)].join('|');

/**
 * What a badge row should actually render for a set of alerts.
 *
 * A single record can carry several `access_equipment` rows - separate authorization records,
 * repeated bulk uploads - and `getAlertsFromAccessEquipments` turns every one of them into an
 * alert. Unit 50962 at Arnold Bros. (Winnipeg) had five, so the field painted five grey circled
 * "D"s side by side. Two different collapses, because the two cases are not the same:
 *
 * - `Default` is the absence of an authorization type, not an authorization. Every Default row
 *   makes the identical statement, so one badge says it. Collapsed on the name ALONE: the rows
 *   differ in `customNote` and `accessPeriod` (each carries its own row's note and period), so a
 *   full-identity match would let all five through again. The surviving badge is the first one,
 *   which keeps that row's note for the tooltip.
 * - Real authorization types collapse only when the badge AND its Note would be identical, so two
 *   rows of the same type carrying different notes or access periods both still render.
 *
 * Deliberately not applied to `option.alerts` / `option.equipmentAlerts` themselves: those arrays
 * have to stay intact because `getFieldColorFromAlerts` branches on the raw length.
 */
export const getDisplayableAlerts = (alerts) => {
    if (!Array.isArray(alerts)) return [];

    const seen = new Set();
    let defaultRendered = false;

    return alerts.filter((alert) => {
        if (!alert) return false;

        if (alert.name === ALERT_NAME_DEFAULT) {
            if (defaultRendered) return false;
            defaultRendered = true;
            return true;
        }

        const key = getAlertKey(alert);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export const getFieldColorFromAlerts = (alerts) => {
    if (!Array.isArray(alerts) || alerts.length === 0) return null;

    if (alerts.length === 1) {
        const [alert] = alerts;
        if (!alert?.color || alert?.name === ALERT_NAME_DEFAULT) return null;
        return alert.color;
    }

    const banned = alerts.find((alert) => alert?.name === ALERT_NAME_BANNED);
    return banned?.color ?? null;
};

const getTrailerSiblingField = (dataField) => (dataField === 'trailer.trailerNumber' ? 'trailer.trailerNumberTwo' : 'trailer.trailerNumber');

// `item` is the stored inventory record: copy `data` before deleting from it.
const cleanTrailerItem = (item) => {
    const cleanItem = { ...item };
    delete cleanItem['trailer.trailerNumberTwo'];
    if (cleanItem.data) {
        cleanItem.data = { ...cleanItem.data };
        delete cleanItem.data['trailer.trailerNumber'];
        delete cleanItem.data['trailer.trailerNumberTwo'];
    }
    return cleanItem;
};

export const extractReferenceFieldsFromItem = (item) => {
    const { data, inventoryItemType } = item;
    const referenceFields = inventoryItemType?.transactionMappingRule?.referenceFields;

    return data && referenceFields ? { data, referenceFields } : { data: null, referenceFields: null };
};

export const getInventoryValuesByField = (inventoryData = [], dataField) =>
    inventoryData.reduce((acc, item) => {
        const { data, referenceFields } = extractReferenceFieldsFromItem(item);
        if (!data || !referenceFields?.includes(dataField)) return acc;

        const value = data[dataField];
        if (value !== null && value !== undefined) {
            acc.push({ ...item, duplicate: item.id, [dataField]: value });
        }
        return acc;
    }, []);

export const getTrailerValuesByField = (inventoryData = [], dataField) => {
    if (!TRAILER_NUMBER_FIELDS.includes(dataField)) return [];

    return inventoryData.reduce((acc, item) => {
        const { data, referenceFields } = extractReferenceFieldsFromItem(item);
        if (!data || !referenceFields?.includes(dataField)) return acc;

        const trailerValue = data['trailer.trailerNumber'];
        if (trailerValue) {
            acc.push({ ...cleanTrailerItem(item), id: item.id, duplicate: item.id, [dataField]: trailerValue });
        }
        return acc;
    }, []);
};

const groupEquipmentByAuthorizedId = (equipmentData = {}) => {
    const { accessEquipments = [], personnel = [], gates = [] } = equipmentData;

    const personnelMap = groupBy(
        personnel.map((p) => ({ ...p, name: `${p.firstName || ''} ${p.lastName || ''}`.trim() })),
        'id'
    );
    const gateMap = groupBy(gates, 'id');

    const grouped = {};

    for (const item of accessEquipments) {
        const { authorizedEquipment, authorizedToPersonnelIds, gateIds } = item;
        if (!authorizedEquipment?.id) continue;

        const equipmentId = authorizedEquipment.id;
        if (!grouped[equipmentId]) {
            grouped[equipmentId] = { ...authorizedEquipment, personnelList: [], gateList: [], accessEquipments: [] };
        }

        grouped[equipmentId].personnelList.push(...(authorizedToPersonnelIds || []).map((id) => personnelMap[id]?.[0]).filter(Boolean));
        grouped[equipmentId].gateList.push(...(gateIds || []).map((id) => gateMap[id]?.[0]).filter(Boolean));
        grouped[equipmentId].accessEquipments.push({
            id: item.id,
            authType: item.authType,
            note: item.note,
            equipmentStatus: item.equipmentStatus,
            monitoringObjectIds: item.monitoringObjectIds,
            createdBy: item.createdBy,
            modifiedBy: item.modifiedBy,
            accessPeriod: {
                accessType: item.accessType,
                startTime: item.startTiem,
                endTime: item.endTime,
                weekdays: item.weekdays,
                periodType: item.periodType,
            },
        });
    }

    return Object.values(grouped).map((eq) => ({
        ...eq,
        personnelList: uniqBy(eq.personnelList, 'id'),
        gateList: uniqBy(eq.gateList, 'id'),
        equipmentAlerts: getAlertsFromAccessEquipments(eq.accessEquipments),
    }));
};

const toEquipmentValues = (groupedEquipment, dataField) =>
    groupedEquipment.reduce((acc, eq) => {
        if (eq.equipmentNumber != null) {
            acc.push({ ...eq, duplicate: eq.id, [dataField]: eq.equipmentNumber });
        }
        return acc;
    }, []);

export const getEquipmentValuesByField = (equipmentData, dataField) => toEquipmentValues(groupEquipmentByAuthorizedId(equipmentData), dataField);

const buildLookup = (dataSource, field) => ({
    dataSource,
    valueExpr: field.dataField,
    displayExpr: field.dataField,
    referenceExpr: 'id',
});

const buildFieldLookup = ({ data, formFields, getValuesFn, recentValueData, filterEquipmentCategory = false }) =>
    formFields?.map((field) => {
        if (!INVENTORY_PROPERTIES.includes(field.dataField)) return field;

        let dataSource = markAndSortRecent(getValuesFn(data, field.dataField), field, recentValueData);

        if (filterEquipmentCategory && EQUIPMENT_CATEGORY_FILTERS[field.dataField]) {
            const allowedCategories = EQUIPMENT_CATEGORY_FILTERS[field.dataField];
            dataSource = dataSource.filter((item) => {
                const cat = item.equipmentCategory?.parentCategory?.name;
                return cat && allowedCategories.includes(cat);
            });
        }

        return { ...field, lookup: buildLookup(dataSource, field), referenceField: 'id' };
    });

export const buildInventoryLookup = (inventoryData, formFields, recentValueData) => {
    const lookups = buildFieldLookup({
        data: inventoryData,
        formFields,
        getValuesFn: (data, field) =>
            TRAILER_NUMBER_FIELDS.includes(field) ? getTrailerValuesByField(data, field) : getInventoryValuesByField(data, field),
        recentValueData,
        filterEquipmentCategory: false,
    });
    return mergeMissingRecentToLookups(formFields, lookups, recentValueData);
};

export const buildEquipmentLookup = (equipmentData, formFields, recentValueData) => {
    const lookups = buildFieldLookup({
        data: groupEquipmentByAuthorizedId(equipmentData),
        formFields,
        getValuesFn: toEquipmentValues,
        recentValueData,
        filterEquipmentCategory: true,
    });
    return mergeMissingRecentToLookups(formFields, lookups, recentValueData);
};

export const buildEffectiveLookup = (lookup, dataField, values) => {
    if (!lookup?.dataSource || !TRAILER_NUMBER_FIELDS.includes(dataField)) return lookup;

    const siblingValue = values[getTrailerSiblingField(dataField)];
    if (!siblingValue) return lookup;

    return {
        ...lookup,
        dataSource: lookup.dataSource.filter((opt) => opt[dataField] !== siblingValue),
    };
};

export const buildCombinedLookup = (inventoryData, equipmentData, formFields, recentValueData) => {
    if (!Array.isArray(formFields)) return [];

    const inventoryLookups = buildInventoryLookup(inventoryData, formFields, recentValueData);
    const equipmentLookups = buildEquipmentLookup(equipmentData, formFields, recentValueData);

    return formFields.map((field, idx) => {
        // if it is not inventory or equipment field, return the field as is
        if (!INVENTORY_PROPERTIES.includes(field.dataField) && !EQUIPMENT_CATEGORY_FILTERS[field.dataField]) return field;

        const invField = inventoryLookups[idx];
        const eqField = equipmentLookups[idx];
        const invSource = invField.lookup?.dataSource || [];
        const eqSource = eqField.lookup?.dataSource || [];

        if (invSource.length && eqSource.length) {
            const valueExpr = invField.lookup?.valueExpr || invField.lookup?.displayExpr || field.dataField;
            const seen = new Map();
            const allItems = [...invSource, ...eqSource];
            for (const item of allItems) {
                const val = item[valueExpr] || item;
                if (seen.has(val)) {
                    if (INVENTORY_PROPERTIES.includes(field.dataField)) {
                        const prev = seen.get(val);
                        const merged = {
                            ...prev,
                            ...item,
                            [field.dataField]: val,
                            isInventoryAndEquipment: true,
                        };
                        seen.set(val, merged);
                    }
                } else {
                    seen.set(val, item);
                }
            }
            let uniqueDataSource = Array.from(seen.values());
            return {
                ...field,
                lookup: { ...invField.lookup, dataSource: uniqueDataSource },
                referenceField: field.referenceField || 'id',
            };
        }

        return invSource.length ? invField : eqSource.length ? eqField : field;
    });
};

export const markAndSortRecent = (dataSource = [], field, recentValueData) => {
    const recent = recentValueData?.find((r) => r.dataField === field.dataField);
    if (!recent?.lookup?.dataSource) return dataSource;

    const recentValues = new Set(recent.lookup.dataSource.map((item) => item.value));
    const flagged = dataSource.map((item) => (recentValues.has(item[field.dataField]) ? { ...item, isRecent: true } : item));

    return [...flagged.filter((i) => i.isRecent), ...flagged.filter((i) => !i.isRecent)];
};

const mergeMissingRecentToLookups = (formFields, lookups, recentValueData) => {
    return formFields?.map((field, idx) => {
        const lookupField = lookups[idx];
        const dataSource = lookupField.lookup?.dataSource || [];
        const recent = recentValueData?.find((r) => r.dataField === field.dataField);
        if (!recent?.lookup?.dataSource) return lookupField;
        const valueExpr = lookupField.lookup?.valueExpr || field.dataField;
        const existingValues = new Set(dataSource.map((item) => item[valueExpr]));
        const missingRecent = recent.lookup.dataSource
            .filter((item) => !existingValues.has(item.value))
            .map((item) => ({
                ...item,
                duplicate: item.id || item.value,
                [field.dataField]: item.value,
                isRecent: true,
            }));
        if (!missingRecent.length) return lookupField;
        const alreadyRecent = dataSource.filter((item) => item.isRecent);
        const notRecent = dataSource.filter((item) => !item.isRecent);
        return {
            ...lookupField,
            lookup: {
                ...lookupField.lookup,
                dataSource: [...alreadyRecent, ...missingRecent, ...notRecent],
            },
        };
    });
};

/**
 * Whether a field accepts a value that is not one of its options.
 *
 * One function because three places act on this and they have to agree: the control's `freeSolo`,
 * the handler that commits what an Agent typed, and CTRL+Enter's fill. While they disagreed, a
 * location-configured open list rendered as free-solo but the typed text never reached the form -
 * the field stayed empty and quick submit overwrote it with '/'.
 *
 * True for a plain input, for a lookup that resolves by reference, and for a list the location
 * opened with `restrictToOptions: false`. Absent means closed, which is the pre-configuration
 * behaviour of every dropdown.
 */
export const acceptsTypedValue = ({ lookup, referenceField, restrictToOptions } = {}) =>
    !lookup?.displayExpr || !!referenceField || restrictToOptions === false;

/** Appended to a retired option's displayed label. Display only - never part of the saved value. */
export const RETIRED_OPTION_LABEL_SUFFIX = '(no longer available)';

/** The displayed UNKNOWN label, which also reaches transaction columns as a stored value. */
const UNKNOWN_DISPLAY_BLANK = '/';

/**
 * Re-add a transaction's stored value to a field's lookup when the location has since deactivated it.
 *
 * The backend cannot do this: `/transactions/trucks/form/fields` is given `gateId`, `locationId` and
 * `companyId` but no transaction id, so it has no way to know which historical value to keep. Its
 * lookup therefore holds active options only, and an older transaction whose value has been retired
 * would render as an empty field.
 *
 * Only ever adds the value the transaction already holds, so a retired option cannot appear when
 * creating a new transaction. The injected option carries `retired: true`, and its label is suffixed
 * so an Agent can see the value is no longer offered. The suffix is display only: the option's value
 * is the raw stored value, and the save path resolves display back to value through this same lookup
 * (`resolveLookupValueFromDisplay`), so the transaction re-saves unchanged.
 *
 * Lookups that resolve by reference are left alone - see the `referenceField` guard below.
 *
 * @param {Array} fields    form field definitions from the form-config response
 * @param {object} transactionData  the transaction being opened
 * @returns {Array} fields, with lookups extended where a stored value was missing
 */
export const preserveRetiredLookupValues = (fields = [], transactionData) => {
    if (!transactionData) return fields;

    return fields.map((field) => {
        const dataSource = field?.lookup?.dataSource;
        if (!Array.isArray(dataSource) || !field.dataField) return field;

        const storedValue = readFromObj(transactionData, field.dataField);
        if (storedValue === PROPERTY_NOT_FOUND || storedValue == null || storedValue === '') return field;
        // Object-valued lookups (inventory, equipment) resolve by reference, not by a scalar value.
        if (typeof storedValue === 'object') return field;
        // person.fullName and person.companyName carry a referenceField: their value points at an
        // authorized person or organization, so a value with no record behind it is an Agent's free
        // input rather than something the location retired, and the editor renders it from the stored
        // value without help. An option invented for it is a record with no id and no authorization,
        // and the person branch of createFormFieldsByTransactionIdSelector turns every option into an
        // alert badge that renders the first letter of the authorization name - BEAST-13427, opening
        // the dropdown threw on the name the invented record does not have.
        if (field.referenceField) return field;
        // A blank marker means "unknown", not "a value this location has retired", and it is absent from
        // most lookups by design - injecting it labels a legitimate blank as no-longer-available.
        // `isUnknownSentinel` covers '?', '-1' and 'UNKNOWN'; '/' is the displayed UNKNOWN label
        // (messages.properties `YesNoUnknown.UNKNOWN=/`) and reaches the column as a stored value, which
        // is why prepareTransactionForSubmit special-cases it too. Not added to UNKNOWN_SENTINEL_VALUES
        // because three other call sites read that list and none of them wants '/' folded in.
        if (isUnknownSentinel(storedValue) || storedValue === UNKNOWN_DISPLAY_BLANK) return field;

        const valueExpr = field.lookup.valueExpr ?? 'value';
        const displayExpr = field.lookup.displayExpr ?? valueExpr;
        if (dataSource.some((option) => option?.[valueExpr] === storedValue)) return field;

        // The option has to stay in dataSource or the field renders blank, so it stays selectable.
        // Marking the label is what discourages re-picking it. Two cases never get the mark:
        // where displayExpr and valueExpr are the same key the label IS the stored value and a suffix
        // would change what gets saved; and on a list the location left open, where a value missing
        // from the options is an Agent's free input rather than something the location retired -
        // calling that "no longer available" is simply wrong.
        const typedIn = acceptsTypedValue(field);
        const label = displayExpr === valueExpr || typedIn ? storedValue : `${storedValue} ${RETIRED_OPTION_LABEL_SUFFIX}`;

        return {
            ...field,
            lookup: {
                ...field.lookup,
                dataSource: [...dataSource, { [valueExpr]: storedValue, [displayExpr]: label, ...(typedIn ? {} : { retired: true }) }],
            },
        };
    });
};
